import { getSourceRoomId } from "building-types/components/componentInstanceUtils";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type {
  FloorAssembly,
  FloorAssemblyHashParameters,
  FloorEdgeOverridesTable,
} from "building-types/floors/floorAssemblyTypes";
import { StructuralFloorType } from "building-types/floors/floorComponentsTypes";
import type {
  Floor,
  FloorPath,
  FloorPathOffsetSettings,
  FloorPlate,
  SolidFloor,
  SolidFramingFloor,
  SolidSlabFloor,
  VoidFloor,
} from "building-types/floors/floorTypes";
import { FloorType } from "building-types/floors/floorTypes";
import type { FoundationAssembly } from "building-types/foundations/foundationAssemblyEntityTypes";
import { SlabType } from "building-types/foundations/foundationEnums";
import type { Framing } from "building-types/framing/framingTypes";
import type {
  FloorEdgeHandle,
  FloorBayEdgeHandle,
  FloorBayHandle,
  FloorOpeningEdgeHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { FloorHandle } from "building-types/handles/docEntityHandleTypes";
import type { TargetedPorchSettings } from "building-types/porches/porch";
import type { RoomClass } from "building-types/rooms/roomTypesEnum";
import { RoomEnclosure, RoomType } from "building-types/rooms/roomTypesEnum";
import type { PorchSettingsBase } from "building-types/settings/porchSettingsTypes";
import { PathPriority } from "building-types/walls/pathPriority";
import { WallType } from "building-types/walls/wallEnums";
import { WallCondition } from "building-types/walls/wallType";
import type { Wall } from "building-types/walls/wallTypes";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { buildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import type { BRep, Extrusion } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { polyLine2CompareEdges, polyLine2EdgeCount } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { slabTypeAtRoomClass } from "../foundation/slabUtils";
import { porchPerimeterOffset } from "../porch/porch";
import { getWallPath } from "../walls/wall";
import { wallPathOpposite } from "../walls/wallDescriptor";
import { solidWallFramingLayer, solidWallSubstrateLayer, wallLayerWithFallback } from "../walls/wallLayers";
import { applyOpeningEdgeOverrides, floorPerimeterInfoToUse, isFloorTransitionEdge } from "./floorMirrorUtils";
import { extrudeFraming } from "./framingUtils";

export function floorPathNew({
  doors = [],
  ...pathProps
}: Partial<FloorPath> & Required<Omit<FloorPath, "doors">>): FloorPath {
  return {
    doors,
    ...pathProps,
  };
}

export function detectFloorPathOffsets({
  wall,
  foundationAssembly,
  settings,
  targetedPorchSettings,
}: {
  wall: Wall;
  leftFloorAssembly: FloorAssembly;
  rightFloorAssembly: FloorAssembly;
  foundationAssembly: FoundationAssembly | undefined;
  settings: FloorPathOffsetSettings;
  targetedPorchSettings: TargetedPorchSettings;
}) {
  const floorOffset = detectWallPathFloorPerimeterOffset({
    wall,
    foundationAssembly,
    settings,
    targetedPorchSettings,
  });

  assertIsNotNullOrUndefined(foundationAssembly);
  const floorAlignmentLayer = foundationAssembly.lateralDatumAbstraction.resolveWallLayer();
  const { leftOffset, rightOffset } = wallLayerWithFallback({ wall, wallLayer: floorAlignmentLayer });

  return {
    floorOffset,
    leftWallOffset: leftOffset,
    rightWallOffset: rightOffset,
  };
}

/**
 *
 * Floor Perimeter offsets are used to determine the location of foundation walls and floor system boundaries on FloorPaths,
 * that don't always match up with the outide edge of the wall
 *
 */
function detectWallPathFloorPerimeterOffset({
  wall,
  foundationAssembly,
  settings,
  targetedPorchSettings,
}: {
  wall: Wall;
  foundationAssembly: FoundationAssembly | undefined;
  settings: FloorPathOffsetSettings;
  targetedPorchSettings: TargetedPorchSettings;
}) {
  const { wallSettings, porchSettings, rearPorchSettings, columnSettings, foundationSettings } = settings;

  let path = getWallPath(wall);
  if (!path.leftRoom && !path.rightRoom) {
    return 0;
  }

  let flip = false;

  const { floorPriority } = path.wallDescriptor;

  if (floorPriority === PathPriority.NONE) {
    return 0;
  } else if (floorPriority === PathPriority.RIGHT) {
    path = wallPathOpposite(path);
    flip = true;
  }

  const { leftRoom, rightRoom, wallCondition } = path;
  const level = leftRoom?.level ?? rightRoom?.level;
  assertIsNotNullOrUndefined(level);
  const { floorIndex } = level;

  let offset: number;

  const foundationSystemSettings = foundationSystemSettingsFromSettingsConfig(foundationSettings);
  assertIsNotNullOrUndefined(leftRoom);
  const foundationSlabType = slabTypeAtRoomClass(leftRoom.attributes.roomClass, foundationSystemSettings);
  assertIsNotNullOrUndefined(foundationAssembly);
  const { lateralDatumAbstraction } = foundationAssembly;

  const foundationPerimeterIsFloorPerimeter = foundationSlabType !== SlabType.NONE && floorIndex <= 0;
  const foundationPerimeterIncludesSubstrate = lateralDatumAbstraction.hasCompatibleAnchor(
    WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE,
  );

  const useWallMassOffset = wallCondition === WallCondition.INTERIOR_FURRING; // use the outer mass for furring walls
  const useWallSubstrateOffset = foundationPerimeterIsFloorPerimeter && foundationPerimeterIncludesSubstrate;

  // assign Wall offsets by default
  // we still need to flip these because we only flipped the path
  if (wall.wallType !== WallType.VOID) {
    let mass = useWallSubstrateOffset ? solidWallSubstrateLayer(wall) : solidWallFramingLayer(wall);
    if (useWallMassOffset) {
      mass = wall.wallType === WallType.SOLID ? wall.mass : wall;
    }
    const { rightOffset, leftOffset } = mass;
    offset = flip ? leftOffset : rightOffset;
  } else {
    const { leftOffset, rightOffset } = wall;
    offset = flip ? leftOffset : rightOffset;
  }

  // check for specific offset conditions
  if (!rightRoom || !isWalledRoomEnclosure[rightRoom.attributes.roomEnclosure]) {
    const leftRoomType = leftRoom.type;
    const leftRoomEnclosure = leftRoom.attributes.roomEnclosure;

    if (leftRoomType === RoomType.FRONT_PORCH || leftRoomType === RoomType.REAR_PORCH) {
      // special case for porch offsets
      let lPorchSettings: PorchSettingsBase | undefined = porchSettings;
      const leftRoomId = getSourceRoomId(leftRoom);
      const targetedSettings = targetedPorchSettings[leftRoomId];
      if (leftRoomType === RoomType.REAR_PORCH) {
        lPorchSettings = targetedSettings ?? rearPorchSettings;
      } else if (leftRoomType === RoomType.FRONT_PORCH) {
        lPorchSettings = targetedSettings ?? porchSettings;
      }

      offset = porchPerimeterOffset(lPorchSettings, columnSettings, undefined, wallSettings, true);
    } else if (leftRoomEnclosure === RoomEnclosure.NONE) {
      // special case for NONE enclosure offsets
      offset = 0;
    }
  }

  if (foundationPerimeterIsFloorPerimeter && !useWallMassOffset) {
    offset += lateralDatumAbstraction.resolveLateralDatum();
  }

  if (flip) {
    return -offset;
  }

  return offset;
}

export function floorPlateNew(props: FloorPlate): FloorPlate {
  return {
    ...props,
  };
}

export function voidFloorNew({ roomClass }: { roomClass: RoomClass }): VoidFloor {
  return {
    floorType: FloorType.NONE,
    roomClass,
  };
}

export function solidFramingFloorNew({
  roomClass,
  hashParams,
  floorElevation,
  floorPlate,
  floorFraming,
  floorPlateThickness,
  subFloor = [],
  subfloorName,
  subfloorType,
  subfloorThickness,
  subfloorComponentId,
  crawlSpace = false,
}: {
  roomClass: RoomClass;
  hashParams: Partial<FloorAssemblyHashParameters>;
  floorElevation: WorldInches;
  floorPlate: FloorPlate;
  floorFraming: Framing[];
  floorPlateThickness: number;
  subFloor?: Extrusion[];
  subfloorName?: SolidFramingFloor["subfloorName"];
  subfloorType?: SolidFramingFloor["subfloorType"];
  subfloorThickness?: SolidFramingFloor["subfloorThickness"];
  subfloorComponentId?: SolidFramingFloor["subfloorComponentId"];
  crawlSpace?: boolean;
}): SolidFramingFloor {
  return {
    ...buildingEntityBaseNew("FRAMED_FLOOR_ASSEMBLY"),
    floorType: FloorType.SOLID,
    structuralType: StructuralFloorType.FRAMING,
    roomClass,
    hashParams,
    floorElevation,
    floorFraming,
    floorPlateThickness,
    floorPlate,
    subFloor,
    subfloorName,
    subfloorType,
    subfloorThickness,
    subfloorComponentId,
    crawlSpace,
  };
}

export function solidSlabFloorNew({
  roomClass,
  hashParams,
  floorElevation,
  floorPlate,
  slab,
  floorPlateThickness,
  subFloor = [],
  subfloorName,
  subfloorType,
  subfloorThickness,
  subfloorComponentId,
  crawlSpace = false,
}: {
  roomClass: RoomClass;
  hashParams: Partial<FloorAssemblyHashParameters>;
  floorElevation: WorldInches;
  floorPlate: FloorPlate;
  slab: Extrusion[];
  floorPlateThickness: number;
  subFloor?: Extrusion[];
  subfloorName?: SolidSlabFloor["subfloorName"];
  subfloorType?: SolidSlabFloor["subfloorType"];
  subfloorThickness?: SolidSlabFloor["subfloorThickness"];
  subfloorComponentId?: SolidSlabFloor["subfloorComponentId"];
  crawlSpace?: boolean;
}): SolidSlabFloor {
  return {
    ...buildingEntityBaseNew("SLAB_FLOOR_ASSEMBLY"),
    floorType: FloorType.SOLID,
    structuralType: StructuralFloorType.SLAB,
    roomClass,
    hashParams,
    floorElevation,
    slab,
    floorPlateThickness,
    floorPlate,
    subFloor,
    subfloorName,
    subfloorType,
    subfloorThickness,
    subfloorComponentId,
    crawlSpace,
  };
}

/**
 * @deprecated - on the path to removal
 */
export function extrudeFloorFraming(floor: SolidFramingFloor): BRep[] {
  const { floorFraming } = floor;

  return extrudeFraming(floorFraming);
}

export function isSolidFloor(floor: Floor): floor is SolidFloor {
  return floor.floorType === FloorType.SOLID;
}

export function isSolidFramingFloor(floor: Floor): floor is SolidFramingFloor {
  return isSolidFloor(floor) && floor.structuralType === StructuralFloorType.FRAMING;
}

export function isSolidSlabFloor(floor: Floor): floor is SolidSlabFloor {
  return isSolidFloor(floor) && floor.structuralType === StructuralFloorType.SLAB;
}

/**
 * Extract the edge-ID slice for a single perimeter from the combined `edgeIds` array
 * stored in `FloorBoundaryFields`. If `perimeterEdgeCounts` is undefined the combined array
 * is assumed to belong entirely to perimeter 0.
 */
export function getEdgeIdsForPerimeter(
  edgeIds: string[],
  perimeterEdgeCounts: number[] | undefined,
  perimeterIndex: number,
): string[] {
  if (!perimeterEdgeCounts) {
    return perimeterIndex === 0 ? edgeIds : [];
  }
  let start = 0;
  for (let i = 0; i < perimeterIndex; i++) {
    start += perimeterEdgeCounts[i] ?? 0;
  }
  const count = perimeterEdgeCounts[perimeterIndex] ?? 0;
  return edgeIds.slice(start, start + count);
}

/** Cumulative edge count before the given perimeter/opening index. */
export function getEdgeGlobalOffset(edgeCounts: number[] | undefined, index: number): number {
  if (!edgeCounts) {
    return 0;
  }
  let offset = 0;
  for (let i = 0; i < index; i++) {
    offset += edgeCounts[i] ?? 0;
  }
  return offset;
}

/** 1-based global key for a perimeter edge, used to index into `floorEdgeOverrides`. */
export function getPerimeterEdgeGlobalKey(
  perimeterEdgeCounts: number[],
  perimeterIndex: number,
  edgeId: number,
): number {
  return getEdgeGlobalOffset(perimeterEdgeCounts, perimeterIndex) + edgeId + 1;
}

export function getFloorEdgeHandles(
  floorHandle: FloorHandle,
  edgeIds: string[],
  perimeterEdgeCounts: number[] = [edgeIds.length],
): FloorEdgeHandle[] {
  return buildPerimeterOutlineEdgeHandleRefs(edgeIds, perimeterEdgeCounts).map(
    ({ perimeterIndex, edgeId, stableEdgeId }) => ({
      parent: floorHandle,
      shapeId: "outline",
      edgeId,
      type: "EDGE",
      perimeterIndex,
      stableEdgeId,
    }),
  );
}

interface PerimeterOutlineEdgeHandleRecord {
  readonly perimeterIndex: number;
  readonly edgeId: number;
  readonly stableEdgeId: string;
}

function buildPerimeterOutlineEdgeHandleRefs(
  edgeIds: string[],
  perimeterEdgeCounts: number[],
): PerimeterOutlineEdgeHandleRecord[] {
  const refs: PerimeterOutlineEdgeHandleRecord[] = [];
  let edgeStart = 0;
  for (let perimeterIndex = 0; perimeterIndex < perimeterEdgeCounts.length; perimeterIndex++) {
    const edgeCount = perimeterEdgeCounts[perimeterIndex];
    for (let localEdgeIndex = 0; localEdgeIndex < edgeCount; localEdgeIndex++) {
      const globalIndex = edgeStart + localEdgeIndex;
      if (globalIndex >= edgeIds.length) {
        break;
      }
      refs.push({
        perimeterIndex,
        edgeId: localEdgeIndex,
        stableEdgeId: edgeIds[globalIndex],
      });
    }
    edgeStart += edgeCount;
  }
  const lastPerimeterIndex = Math.max(0, perimeterEdgeCounts.length - 1);
  for (let globalEdgeId = edgeStart; globalEdgeId < edgeIds.length; globalEdgeId++) {
    refs.push({
      perimeterIndex: lastPerimeterIndex,
      edgeId: globalEdgeId - edgeStart,
      stableEdgeId: edgeIds[globalEdgeId],
    });
  }
  return refs;
}

export function getBayEdgeHandles(
  bayHandle: FloorBayHandle,
  edgeIds: string[],
  perimeterEdgeCounts: number[] = [edgeIds.length],
): FloorBayEdgeHandle[] {
  return buildPerimeterOutlineEdgeHandleRefs(edgeIds, perimeterEdgeCounts).map(
    ({ perimeterIndex, edgeId, stableEdgeId }) => ({
      parent: bayHandle,
      shapeId: "outline",
      edgeId,
      type: "EDGE",
      perimeterIndex,
      stableEdgeId,
    }),
  );
}

/** Offset-expanded geometry shared by parent-floor and bay generated boundaries. */
export interface ExpandedGeneratedFloorBoundaryEdges {
  readonly perimeters: PolyLine2[];
  readonly edgeIds: string[];
  readonly perEdgeIds: string[][];
  readonly perimeterEdgeCounts: number[];
}

export function expandGeneratedFloorBoundaryEdges(
  perimeters: PolyLine2[],
  combinedEdgeIds: string[],
  perimeterEdgeCounts: number[] | undefined,
  floorEdgeOverrides: FloorEdgeOverridesTable | undefined,
  offset: number | undefined,
): ExpandedGeneratedFloorBoundaryEdges {
  const expandedPerimeters: PolyLine2[] = [];
  const allExpandedEdgeIds: string[] = [];
  const perEdgeIds: string[][] = [];
  const expandedPerimeterEdgeCounts: number[] = [];

  for (let i = 0; i < perimeters.length; i++) {
    const perimeter = perimeters[i];
    const edgeIdsForPerimeter = getEdgeIdsForPerimeter(combinedEdgeIds, perimeterEdgeCounts, i);
    const { perimeter: perimeterToUse, edgeIds: perimeterEdgeIdsToUse } = floorPerimeterInfoToUse(
      perimeter,
      edgeIdsForPerimeter,
      floorEdgeOverrides,
      offset,
    );
    expandedPerimeters.push(perimeterToUse);
    perEdgeIds.push(perimeterEdgeIdsToUse);
    allExpandedEdgeIds.push(...perimeterEdgeIdsToUse);
    expandedPerimeterEdgeCounts.push(perimeterEdgeIdsToUse.length);
  }

  return {
    perimeters: expandedPerimeters,
    edgeIds: allExpandedEdgeIds,
    perEdgeIds,
    perimeterEdgeCounts: expandedPerimeterEdgeCounts,
  };
}

export interface PerimeterOutlineEdgeLabelDescriptor<THandle extends PerimeterOutlineEdgeHandleRef> {
  readonly handle: THandle;
  readonly edgeIndex: number;
  readonly perimeterIndex: number;
  readonly labelText: string;
}

export interface PerimeterOutlineEdgeHandleRef {
  readonly perimeterIndex: number;
  readonly edgeId: number;
}

/** Numbered perimeter edge labels, skipping synthetic transition edges from offset joins. */
export function collectLabeledPerimeterOutlineEdgeLabels<THandle extends PerimeterOutlineEdgeHandleRef>(
  expanded: ExpandedGeneratedFloorBoundaryEdges & { readonly handles: readonly THandle[] },
): PerimeterOutlineEdgeLabelDescriptor<THandle>[] {
  const perimeterLabelCounters: number[] = new Array(expanded.perEdgeIds.length).fill(0);
  const labels: PerimeterOutlineEdgeLabelDescriptor<THandle>[] = [];

  for (const handle of expanded.handles) {
    const perimeterEdgeIds = expanded.perEdgeIds[handle.perimeterIndex];
    if (perimeterEdgeIds && isFloorTransitionEdge(perimeterEdgeIds[handle.edgeId])) {
      continue;
    }
    perimeterLabelCounters[handle.perimeterIndex]++;
    labels.push({
      handle,
      edgeIndex: handle.edgeId,
      perimeterIndex: handle.perimeterIndex,
      labelText: String(perimeterLabelCounters[handle.perimeterIndex]),
    });
  }

  return labels;
}

export interface PerimeterBoundarySection<THandle> {
  readonly title: string;
  readonly handles: readonly THandle[];
  readonly edgeIds: readonly string[];
}

/** Groups offset-expanded perimeter handles by source boundary ring. */
export function buildPerimeterBoundarySections<THandle>(
  expanded: ExpandedGeneratedFloorBoundaryEdges & { readonly handles: readonly THandle[] },
  singleSectionTitle: string,
  multiSectionTitlePrefix: string,
): PerimeterBoundarySection<THandle>[] {
  const sections: PerimeterBoundarySection<THandle>[] = [];
  let handleStart = 0;
  const multiplePerimeters = expanded.perimeterEdgeCounts.length > 1;

  for (let perimeterIndex = 0; perimeterIndex < expanded.perimeterEdgeCounts.length; perimeterIndex++) {
    const edgeCount = expanded.perimeterEdgeCounts[perimeterIndex];
    sections.push({
      title: multiplePerimeters ? `${multiSectionTitlePrefix} ${perimeterIndex + 1}` : singleSectionTitle,
      handles: expanded.handles.slice(handleStart, handleStart + edgeCount),
      edgeIds: expanded.perEdgeIds[perimeterIndex],
    });
    handleStart += edgeCount;
  }

  return sections;
}

export interface ExpandedBayEdgeInfo extends ExpandedGeneratedFloorBoundaryEdges {
  readonly handles: FloorBayEdgeHandle[];
}

/** Applies generated-boundary edge overrides to bay perimeter polylines. */
export function expandBayEdgeIds(
  bayHandle: FloorBayHandle,
  perimeters: PolyLine2[],
  combinedEdgeIds: string[],
  perimeterEdgeCounts: number[] | undefined,
  floorEdgeOverrides: FloorEdgeOverridesTable | undefined,
  offset: number | undefined,
): ExpandedBayEdgeInfo {
  const expanded = expandGeneratedFloorBoundaryEdges(
    perimeters,
    combinedEdgeIds,
    perimeterEdgeCounts,
    floorEdgeOverrides,
    offset,
  );
  return {
    ...expanded,
    handles: getBayEdgeHandles(bayHandle, expanded.edgeIds, expanded.perimeterEdgeCounts),
  };
}

/** Slices the combined `openingEdgeIds` array for a specific opening. */
export function getEdgeIdsForOpening(
  openingEdgeIds: string[],
  openingEdgeCounts: number[] | undefined,
  openingIndex: number,
): string[] {
  if (!openingEdgeCounts) {
    return openingIndex === 0 ? openingEdgeIds : [];
  }
  let start = 0;
  for (let i = 0; i < openingIndex; i++) {
    start += openingEdgeCounts[i] ?? 0;
  }
  const count = openingEdgeCounts[openingIndex] ?? 0;
  return openingEdgeIds.slice(start, start + count);
}

export function getFloorOpeningEdgeHandles(
  floorHandle: FloorHandle,
  openings: PolyLine2[],
  openingIndex: number,
): FloorOpeningEdgeHandle[] {
  const opening = openings[openingIndex];

  if (!opening) {
    return [];
  }

  // Generate edge handles for each edge in the opening
  const edgeCount = opening.length / 2 - 1; // -1 because last point repeats first point
  const edgeHandles: FloorOpeningEdgeHandle[] = [];

  for (let i = 0; i < edgeCount; i++) {
    edgeHandles.push({
      parent: floorHandle,
      shapeId: "opening",
      edgeId: i,
      type: "EDGE",
      openingIndex,
    });
  }

  return edgeHandles;
}

/**
 * Builds a new edgeIds array for a changed set of floor perimeters by matching edges
 * geometrically against the original perimeters. Edges that match geometrically keep
 * their original stable ID; new edges (from splits or new geometry) get fresh IDs.
 *
 * Follows the same pattern as rooms' `buildEdgeIdMap` in roomEdgeMaps.ts.
 *
 * @param oldPerimeters  Perimeters before the edit
 * @param oldEdgeIds     Flat combined edge IDs before the edit
 * @param newPerimeters  Perimeters after the edit
 * @param nextIdCounter  Starting counter for generating fresh IDs (default: max existing + 1)
 */
export function buildFloorEdgeIdMap(
  oldPerimeters: PolyLine2[],
  oldEdgeIds: string[],
  newPerimeters: PolyLine2[],
  nextIdCounter?: number,
): string[] {
  let counter =
    nextIdCounter ??
    oldEdgeIds.reduce((max, id) => {
      const n = parseInt(id, 10);
      return Number.isFinite(n) ? Math.max(max, n) : max;
    }, 0) + 1;

  const usedOldIds = new Set<string>();
  const newEdgeIds: string[] = [];

  for (const newPl of newPerimeters) {
    const newCount = polyLine2EdgeCount(newPl);

    for (let j = 0; j < newCount; j++) {
      let matched = false;

      let oldOffset = 0;
      for (const oldPl of oldPerimeters) {
        const oldCount = polyLine2EdgeCount(oldPl);

        for (let i = 0; i < oldCount; i++) {
          const oldId = oldEdgeIds[oldOffset + i];
          if (oldId !== undefined && !usedOldIds.has(oldId) && polyLine2CompareEdges(newPl, j, oldPl, i)) {
            newEdgeIds.push(oldId);
            usedOldIds.add(oldId);
            matched = true;
            break;
          }
        }

        if (matched) {
          break;
        }
        oldOffset += oldCount;
      }

      if (!matched) {
        newEdgeIds.push(String(counter++));
      }
    }
  }

  return newEdgeIds;
}

export interface ExpandedFloorEdgeInfo extends ExpandedGeneratedFloorBoundaryEdges {
  readonly handles: FloorEdgeHandle[];
}

export function expandFloorEdgeIds(
  floorHandle: FloorHandle,
  perimeters: PolyLine2[],
  combinedEdgeIds: string[],
  perimeterEdgeCounts: number[] | undefined,
  floorEdgeOverrides: FloorEdgeOverridesTable | undefined,
  offset: number | undefined,
): ExpandedFloorEdgeInfo {
  const expanded = expandGeneratedFloorBoundaryEdges(
    perimeters,
    combinedEdgeIds,
    perimeterEdgeCounts,
    floorEdgeOverrides,
    offset,
  );
  return {
    ...expanded,
    handles: getFloorEdgeHandles(floorHandle, expanded.edgeIds, expanded.perimeterEdgeCounts),
  };
}

export interface ExpandedOpeningEdgeInfo {
  openings: PolyLine2[];
  perOpeningEdgeIds: string[][];
  perOpeningHandles: FloorOpeningEdgeHandle[][];
}

/**
 * Expands opening edge IDs through `applyOpeningEdgeOverrides`, producing
 * handles with `edgeId` values that match the offset polyline indices
 * (consistent with viewport label handles in EditorViewerLabels and
 * FloorEdgeSelectionManip). Transition edges are included in the edge ID
 * arrays but excluded from the returned handles so callers can filter them
 * the same way `FloorPerimeterSection` does for perimeter edges.
 */
export function expandFloorOpeningEdgeIds(
  floorHandle: FloorHandle,
  openings: PolyLine2[],
  openingEdgeCounts: number[] | undefined,
  openingEdgeIds: string[] | undefined,
  floorOpeningEdgeOverrides: FloorEdgeOverridesTable | undefined,
): ExpandedOpeningEdgeInfo {
  const result = applyOpeningEdgeOverrides(openings, openingEdgeCounts, openingEdgeIds, floorOpeningEdgeOverrides);

  const expandedOpenings: PolyLine2[] = [];
  const perOpeningEdgeIds: string[][] = [];
  const perOpeningHandles: FloorOpeningEdgeHandle[][] = [];

  for (let openingIndex = 0; openingIndex < result.openings.length; openingIndex++) {
    const opening = result.openings[openingIndex];
    if (!opening || opening.length < 4) {
      continue;
    }

    expandedOpenings.push(opening);
    const edgeIds = getEdgeIdsForOpening(result.openingEdgeIds, result.openingEdgeCounts, openingIndex);
    perOpeningEdgeIds.push(edgeIds);

    const handles: FloorOpeningEdgeHandle[] = [];
    for (let edgeId = 0; edgeId < edgeIds.length; edgeId++) {
      if (isFloorTransitionEdge(edgeIds[edgeId])) {
        continue;
      }
      handles.push({
        parent: floorHandle,
        shapeId: "opening",
        edgeId,
        type: "EDGE",
        openingIndex,
      });
    }
    perOpeningHandles.push(handles);
  }

  return { openings: expandedOpenings, perOpeningEdgeIds, perOpeningHandles };
}

export function nonZeroFloorThickness(thickness: number): boolean {
  return thickness > DIST_EPSILON;
}
